import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/Webservice.css";

import icon1 from "../assets/icon1.png";
import icon2 from "../assets/icon2.png";
import icon3 from "../assets/icon3.png";
import icon4 from "../assets/icon4.png";
import icon5 from "../assets/icon5.png";
import icon6 from "../assets/icon6.png";

const webServices = [
  {
    icon: icon1,
    title: "Custom Website Development",
    desc: "We build custom websites that reflect your brand identity and support your business objectives. Every website is developed with clean code, responsive layouts, scalable architecture, and user-focused functionality to ensure long-term performance and growth.",
    tags: [
      "Custom Websites",
      "Responsive Design",
      "Scalable Development",
      "Business Websites",
      "Clean Code",
    ],
  },
  {
    icon: icon2,
    title: "UI/UX Website Design",
    desc: "Our UI/UX designers create intuitive, engaging, and visually appealing website experiences that encourage users to explore, interact, and convert. Every interface is designed with usability, accessibility, and customer satisfaction in mind.",
    tags: [
      "UI Design",
      "UX Design",
      "Wireframing",
      "User Experience",
      "Interface Design",
    ],
  },
  {
    icon: icon3,
    title: "Website Performance Optimization",
    desc: "A fast website improves both search rankings and customer experience. We optimize website speed, Core Web Vitals, page loading performance, code efficiency, and responsiveness to deliver exceptional performance across all devices.",
    tags: [
      "Core Web Vitals",
      "Website Speed",
      "Performance Optimization",
      "Mobile Optimization",
      "Faster Loading",
    ],
  },
  {
    icon: icon4,
    title: "SEO-Ready Website Development",
    desc: "We build websites with technical SEO integrated from the ground up. Our development process includes SEO-friendly URLs, schema-ready structures, optimized metadata, clean code, fast loading pages, and mobile responsiveness to improve search engine visibility.",
    tags: [
      "Technical SEO",
      "SEO-Friendly Websites",
      "Schema Ready",
      "Search Optimization",
      "Organic Growth",
    ],
  },
  {
    icon: icon5,
    title: "Website Migration & Integration",
    desc: "Need to migrate your website or integrate third-party tools? We ensure secure website migrations with minimal downtime while integrating CRMs, payment gateways, APIs, analytics platforms, and marketing tools without affecting website performance.",
    tags: [
      "Website Migration",
      "API Integration",
      "CRM Integration",
      "CMS Migration",
      "Secure Transfer",
    ],
  },
  {
    icon: icon6,
    title: "Website Maintenance & Support",
    desc: "Our website maintenance services keep your website secure, updated, and performing at its best. From software updates and bug fixes to security monitoring and performance improvements, we provide ongoing support that keeps your business running smoothly.",
    tags: [
      "Website Maintenance",
      "Security Updates",
      "Bug Fixes",
      "Performance Monitoring",
      "Technical Support",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function WebServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = webServices.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="web-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="web-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`web-services-eyebrow ${sectionVisible ? "web-reveal" : ""}`}
      >
        What We Offer
      </span>

      <h2
        className={`web-services-heading ${sectionVisible ? "web-reveal" : ""}`}
        ref={headingRef}
      >
        Complete Website Development Solutions for Every Business
      </h2>

      <p
        className={`web-services-subtext ${sectionVisible ? "web-reveal" : ""}`}
        ref={subtextRef}
      >
        Whether you're launching a new website or upgrading an existing one,
        our custom website development services are designed to help your
        business grow online. We combine modern technologies, responsive
        design, SEO best practices, and high-performance development to
        create websites that attract visitors, improve engagement, and
        generate more leads.
      </p>

      <div className="web-carousel">
        <button
          type="button"
          className="web-carousel-arrow web-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="web-carousel-track">
          {webServices.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`web-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="web-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="web-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="web-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="web-carousel-arrow web-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="web-carousel-dots">
        {webServices.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`web-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default WebServicesSection;